'use client';

import { useState } from 'react';
import Link from 'next/link';
import { zodResolver } from '@hookform/resolvers/zod';
import { ArrowRight, Eye, EyeOff, Mail } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type { z } from 'zod';

import { Button } from '@kit/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@kit/ui/form';
import { If } from '@kit/ui/if';
import { Input } from '@kit/ui/input';
import { Trans } from '@kit/ui/trans';

import { PasswordSignInSchema } from '../schemas/password-sign-in.schema';

export function PasswordSignInForm({
  onSubmit,
  captchaLoading = false,
  loading = false,
  redirecting = false,
  defaultValues,
  magicLinkEnabled = false,
  onMagicLinkSubmit,
  magicLinkLoading = false,
}: {
  onSubmit: (params: z.infer<typeof PasswordSignInSchema>) => unknown;
  captchaLoading: boolean;
  loading: boolean;
  redirecting: boolean;
  defaultValues?: Partial<z.infer<typeof PasswordSignInSchema>>;
  /**
   * When true, a quiet "Send me a magic link" secondary action renders below
   * the primary button. It reuses the same email field (no duplicate form) and
   * validates the email only — password is not required. Mirrors the sign-up
   * screen and the Meteor sign-in form so all auth screens behave identically.
   */
  magicLinkEnabled?: boolean;
  onMagicLinkSubmit?: (email: string) => unknown;
  magicLinkLoading?: boolean;
}) {
  const { t } = useTranslation('auth');
  const [showPassword, setShowPassword] = useState(false);

  const form = useForm({
    resolver: zodResolver(PasswordSignInSchema),
    defaultValues: {
      email: defaultValues?.email ?? '',
      password: defaultValues?.password ?? '',
    },
  });

  // Magic link reuses the email field — validate email only (no password).
  const onSendMagicLink = async () => {
    const isEmailValid = await form.trigger('email');
    if (!isEmailValid) return;

    onMagicLinkSubmit?.(form.getValues('email'));
  };

  return (
    <Form {...form}>
      <form
        className={'flex w-full flex-col gap-y-4'}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <FormField
          control={form.control}
          name={'email'}
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                <Trans i18nKey={'common:emailAddress'} />
              </FormLabel>

              <FormControl>
                <Input
                  data-test={'email-input'}
                  required
                  type="email"
                  autoFocus
                  placeholder={t('auth:emailPlaceholder')}
                  {...field}
                />
              </FormControl>

              <FormMessage />
            </FormItem>
          )}
        />

        <FormField
          control={form.control}
          name={'password'}
          render={({ field }) => (
            <FormItem>
              <FormLabel>
                <Trans i18nKey={'common:password'} />
              </FormLabel>

              <FormControl>
                <div className="relative">
                  <Input
                    required
                    data-test={'password-input'}
                    type={showPassword ? 'text' : 'password'}
                    placeholder={t('auth:passwordPlaceholder')}
                    className="pr-10"
                    {...field}
                  />

                  <Button
                    type="button"
                    variant="ghost"
                    size="icon"
                    tabIndex={-1}
                    aria-label={t(
                      showPassword ? 'auth:hidePassword' : 'auth:showPassword',
                    )}
                    className="text-muted-foreground hover:text-foreground absolute inset-y-0 right-0 flex h-full items-center pr-3 hover:bg-transparent"
                    onClick={() => setShowPassword((prev) => !prev)}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </Button>
                </div>
              </FormControl>

              <FormMessage />

              <div className="flex justify-end">
                <Link
                  href={'/auth/password-reset'}
                  className="text-muted-foreground hover:text-foreground text-xs underline-offset-4 hover:underline"
                >
                  <Trans i18nKey={'auth:passwordForgottenQuestion'} />
                </Link>
              </div>
            </FormItem>
          )}
        />

        <Button
          data-test="auth-submit-button"
          className={'group w-full'}
          type="submit"
          disabled={loading || redirecting || captchaLoading}
        >
          <If condition={redirecting}>
            <span className={'animate-in fade-in slide-in-from-bottom-24'}>
              <Trans i18nKey={'auth:redirecting'} />
            </span>
          </If>

          <If condition={loading}>
            <span className={'animate-in fade-in slide-in-from-bottom-24'}>
              <Trans i18nKey={'auth:signingIn'} />
            </span>
          </If>

          <If condition={captchaLoading}>
            <span className={'animate-in fade-in slide-in-from-bottom-24'}>
              <Trans i18nKey={'auth:verifyingCaptcha'} />
            </span>
          </If>

          <If condition={!redirecting && !loading && !captchaLoading}>
            <span
              className={
                'animate-in fade-in slide-in-from-bottom-24 flex items-center'
              }
            >
              <Trans i18nKey={'auth:signInWithEmail'} />

              <ArrowRight
                className={
                  'zoom-in animate-in slide-in-from-left-2 fill-mode-both h-4 delay-500 duration-500'
                }
              />
            </span>
          </If>
        </Button>

        {/* Magic link — same email input, no password required. A quiet text
            link (not a full-width button) so it doesn't compete with the
            primary "Sign in" CTA. Mirrors the sign-up screen exactly. */}
        <If condition={magicLinkEnabled}>
          <div className="flex justify-center">
            <Button
              type="button"
              variant="link"
              size="sm"
              className="text-muted-foreground hover:text-foreground h-auto gap-1.5"
              data-test="send-magic-link-button"
              disabled={magicLinkLoading || captchaLoading}
              onClick={onSendMagicLink}
            >
              <Mail className="h-3.5 w-3.5" />
              <If condition={magicLinkLoading}>
                <Trans i18nKey={'auth:sendingEmailLink'} />
              </If>
              <If condition={!magicLinkLoading}>
                <Trans i18nKey={'auth:sendMagicLink'} />
              </If>
            </Button>
          </div>
        </If>
      </form>
    </Form>
  );
}
